// src/components/Auth/LoginForm.jsx
import React, { useState } from 'react';
import '../../styles/components/LoginForm.css';
import { FaArrowRight } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import API_CONFIG from '../../apiConfig';

const findToken = (obj) => {
  if (!obj || typeof obj !== 'object') return null;
  
  if (obj.token && typeof obj.token === 'string') return obj.token;
  if (obj.auth_token && typeof obj.auth_token === 'string') return obj.auth_token;
  if (obj.access_token && typeof obj.access_token === 'string') return obj.access_token;
  if (obj.accessToken && typeof obj.accessToken === 'string') return obj.accessToken;
  
  if (obj.success) {
    if (typeof obj.success === 'string') return obj.success;
    if (typeof obj.success === 'object') {
      const nestedToken = findToken(obj.success);
      if (nestedToken) return nestedToken;
    }
  }
  
  if (obj.data && typeof obj.data === 'object') {
    const nestedToken = findToken(obj.data);
    if (nestedToken) return nestedToken;
  }

  if (obj.Profile && typeof obj.Profile === 'object') {
    const nestedToken = findToken(obj.Profile);
    if (nestedToken) return nestedToken;
  }

  for (const key of Object.keys(obj)) {
    if (obj[key] && typeof obj[key] === 'object') {
      const nestedToken = findToken(obj[key]);
      if (nestedToken) return nestedToken;
    }
  }
  return null;
};

const LoginForm = ({ t, onVerificationRequired, onForgotPassword }) => {
  const navigate = useNavigate();
  const [email,     setEmail]     = useState('');
  const [password,  setPassword]  = useState('');
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState(null);
  const [errorType, setErrorType] = useState(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [isUserDeleted, setIsUserDeleted] = useState(false);

  const getErrorMessage = (errorMessage) => {
    const knownErrors = {
      'Invalid username or password': 'invalid_credentials',
      'Invalid email or password':    'invalid_credentials',
      'User not found':               'user_not_found',
      'Account locked':               'account_locked',
      'Too many attempts':            'too_many_attempts',
      'Network error':                'network_error',
      'Server error':                 'server_error',
      'Invalid credentials':          'invalid_credentials',
      'Wrong password':               'invalid_credentials',
      'Email not registered':         'user_not_found',
    };
    for (const [key, translationKey] of Object.entries(knownErrors)) {
      if (errorMessage.toLowerCase().includes(key.toLowerCase())) {
        return t && typeof t === 'function' ? t(translationKey, errorMessage) : errorMessage;
      }
    }
    return errorMessage;
  };

  const getErrorType = (errorMessage) => {
    const msg = errorMessage.toLowerCase();
    if (msg.includes('invalid') || msg.includes('credentials') || msg.includes('not found') ||
        msg.includes('wrong password') || msg.includes('not registered')) return 'credentials';
    if (msg.includes('locked') || msg.includes('attempts')) return 'account';
    if (msg.includes('network') || msg.includes('connection')) return 'network';
    return 'general';
  };

  const getErrorStyle = (type) => ({
    credentials: { background: '#fff3cd', border: '1px solid #ffc107',  color: '#856404' },
    account:     { background: '#f8d7da', border: '1px solid #dc3545',  color: '#721c24' },
    network:     { background: '#d1ecf1', border: '1px solid #17a2b8',  color: '#0c5460' },
    general:     { background: '#ffeeee', border: '1px solid #ff6b6b',  color: '#721c24' },
  }[type] || { background: '#ffeeee', border: '1px solid #ff6b6b', color: '#721c24' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setErrorType(null);

    try {
      const formData = new URLSearchParams();
      formData.append('email',    email);
      formData.append('password', password);

      const response = await fetch(API_CONFIG.LOGIN_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData,
      });

      const text = await response.text();
      if (text.startsWith('<!DOCTYPE') || text.startsWith('<html')) {
        throw new Error('Server returned HTML instead of JSON');
      }

      let data;
      try { data = JSON.parse(text); } catch { throw new Error('Invalid server response'); }
      console.log("LoginForm - Full Server Response:", data);

      if (data?.error === "User has been deleted") {
        setIsUserDeleted(true);
        setLoading(false);
        return;
      }

      const isUnverified = data && (
                           data.userVerified === false || 
                           data.userVerified === 'false' || 
                           data.userVerified === 0 || 
                           data.success?.userVerified === false || 
                           data.Profile?.userVerified === false || 
                           data.UserDetails?.userVerified === false ||
                           data.Profile?.usr_det?.userVerified === false ||
                           data.user_verified === false || 
                           data.success?.user_verified === false || 
                           data.Profile?.user_verified === false
      );

      if (isUnverified && onVerificationRequired) {
        const token = findToken(data);
        if (token) localStorage.setItem('auth_token', token);
        localStorage.setItem('login_response_full', JSON.stringify(data));
        localStorage.setItem('unverified_email', email);
        onVerificationRequired({
          token: token,
          email: email,
          response: data,
          from: 'login'
        });
        navigate('/verify-email');
      } else {
        if (!response.ok || data?.error || data?.message) {
          throw new Error(data?.error || data?.message || 'Login failed');
        }
        if (!data?.success && !data?.token && !data?.Profile) {
          throw new Error('Invalid response structure');
        }

        const token = findToken(data);
        if (token) localStorage.setItem('auth_token', token);
        if (data.Profile)        localStorage.setItem('user_profile',  JSON.stringify(data.Profile));
        if (data.UserDetails)    localStorage.setItem('user_details',  JSON.stringify(data.UserDetails));
        localStorage.setItem('login_response_full', JSON.stringify(data));

        window.location.href = '/dashboard';
      }
    } catch (err) {
      if (err.message === "User has been deleted" || err.message.toLowerCase().includes("user has been deleted")) {
        setIsUserDeleted(true);
        setLoading(false);
        return;
      }
      const type = getErrorType(err.message);
      setErrorType(type);
      setError(getErrorMessage(err.message));
      setShowErrorModal(true);
      if (type === 'credentials') setPassword('');
    } finally {
      setLoading(false);
    }
  };

  const errorStyle = errorType ? getErrorStyle(errorType) : {};

  return (
    <div className="auth-column login-column">

      <span className="login-title">{t('existing_user', 'Existing User')}</span>
      <p className="login-description">
        {t('enter_email', 'Please Enter Your Registered Email To Proceed With Your Account')}
      </p>

      {error && (
        <div
          className="login-error-message"
          style={{
            ...errorStyle,
            marginBottom: 15, padding: '12px 15px', borderRadius: 8,
            display: 'flex', alignItems: 'center', gap: 10,
            fontSize: 14, fontWeight: 500,
          }}
        >
          <span style={{ fontSize: 20 }}>⚠️</span>
          <span style={{ flex: 1 }}>{error}</span>
          {errorType === 'network' && (
            <button
              onClick={(e) => { e.preventDefault(); handleSubmit(e); }}
              style={{ padding: '5px 10px', border: 'none', borderRadius: 4, background: '#17a2b8', color: 'white', cursor: 'pointer', fontSize: 12 }}
            >
              {t('retry', 'Retry')}
            </button>
          )}
        </div>
      )}

      <form onSubmit={handleSubmit} className="login-form">
        <div className="login-form-group">
          <input
            type="email"
            id="email"
            value={email}
            onChange={(e) => { setEmail(e.target.value); if (errorType === 'credentials') { setError(null); setErrorType(null); } }}
            placeholder={t('email_address', 'Email Address')}
            required
            disabled={loading}
            className={errorType === 'credentials' ? 'input-error' : ''}
          />
          <label htmlFor="email">{t('email_address', 'Email Address')}</label>
        </div>

        <div className="login-form-group">
          <input
            type="password"
            id="password"
            value={password}
            onChange={(e) => { setPassword(e.target.value); if (errorType === 'credentials') { setError(null); setErrorType(null); } }}
            placeholder={t('password_otp', 'Password / OTP')}
            required
            disabled={loading}
            className={errorType === 'credentials' ? 'input-error' : ''}
          />
          <label htmlFor="password">{t('password_otp', 'Password / OTP')}</label>
        </div>

        <div className="login-buttons-row">
          <button
            type="submit"
            className={`login-btn-submit ${loading ? 'loading' : ''}`}
            disabled={loading}
          >
            {loading ? (
              <><span className="spinner" /><span>{t('logging_in', 'Logging in...')}</span></>
            ) : (
              <><span>{t('submit', 'Submit')}</span><FaArrowRight style={{ fontSize: 15 }} stroke="currentColor" strokeWidth="35" /></>
            )}
          </button>
        </div>

        <div className="login-forgot-password" onClick={onForgotPassword} style={{ cursor: 'pointer' }}>
          <span className="login-question-mark">?</span>
          {t('forgot_password', 'If you forgot your email/username or password, click here')}
        </div>
      </form>

      {showErrorModal && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(15, 23, 42, 0.6)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 99999,
        }}>
          <div style={{
            backgroundColor: '#ffffff',
            borderRadius: '16px',
            padding: '28px',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            textAlign: 'center',
            border: '1px solid #fee2e2',
          }}>
            <div style={{
              width: '56px',
              height: '56px',
              borderRadius: '50%',
              backgroundColor: '#fee2e2',
              color: '#ef4444',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px'
            }}>
              <span className="material-icons" style={{ fontSize: '32px' }}>error_outline</span>
            </div>
            <h3 style={{
              fontSize: '1.25rem',
              fontWeight: '700',
              color: '#1e293b',
              marginBottom: '10px'
            }}>
              {t('loginErrorTitle', 'Authentication Error')}
            </h3>
            <p style={{
              fontSize: '0.95rem',
              color: '#64748b',
              lineHeight: '1.5',
              marginBottom: '24px'
            }}>
              {error}
            </p>
            <button 
              type="button"
              onClick={() => setShowErrorModal(false)}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: '#ef4444',
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                fontWeight: '600',
                fontSize: '0.95rem',
                cursor: 'pointer',
              }}
            >
              {t('close', 'Close')}
            </button>
          </div>
        </div>
      )}

      {isUserDeleted && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(15, 23, 42, 0.7)',
          backdropFilter: 'blur(8px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 999999,
        }}>
          <div style={{
            backgroundColor: '#ffffff',
            borderRadius: '20px',
            padding: '40px 32px',
            maxWidth: '480px',
            width: '90%',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            textAlign: 'center',
            border: '1px solid #fee2e2',
            position: 'relative',
            overflow: 'hidden'
          }}>
            {/* Soft red header pattern or stripe */}
            <div style={{
              position: 'absolute',
              top: 0, left: 0, right: 0,
              height: '6px',
              backgroundColor: '#ef4444'
            }} />

            {/* Error Icon */}
            <div style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              backgroundColor: '#fee2e2',
              color: '#ef4444',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px',
              boxShadow: '0 8px 16px rgba(239, 68, 68, 0.1)'
            }}>
              <span className="material-icons" style={{ fontSize: '36px' }}>no_accounts</span>
            </div>

            {/* Title */}
            <h3 style={{
              fontSize: '1.4rem',
              fontWeight: '800',
              color: '#0f172a',
              marginBottom: '12px',
              fontFamily: 'system-ui, sans-serif'
            }}>
              {t('profileDeletedTitle', 'Profile Deleted')}
            </h3>

            {/* Description */}
            <p style={{
              fontSize: '0.975rem',
              color: '#475569',
              lineHeight: '1.6',
              marginBottom: '28px'
            }}>
              {t('profileDeletedDesc', 'Your profile has been deleted. If you would like to restore your account, please contact our support team at:')}
            </p>

            {/* Contact Details Card */}
            <div style={{
              backgroundColor: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: '12px',
              padding: '20px',
              marginBottom: '32px',
              textAlign: 'left',
              display: 'flex',
              flexDirection: 'column',
              gap: '14px'
            }}>
              {/* Email Row */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '8px',
                  backgroundColor: '#e6f1fb',
                  color: '#185fa5',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <span className="material-icons" style={{ fontSize: '18px' }}>mail</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Email</span>
                  <a href="mailto:supportoffice@emiratesscholar.com" style={{ fontSize: '14px', color: '#185fa5', fontWeight: '600', textDecoration: 'none' }}>
                    supportoffice@emiratesscholar.com
                  </a>
                </div>
              </div>

              {/* Phone Row */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '8px',
                  backgroundColor: '#e6f1fb',
                  color: '#185fa5',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <span className="material-icons" style={{ fontSize: '18px' }}>phone</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Contact Number</span>
                  <a href="tel:+97124093159" style={{ fontSize: '14px', color: '#0f172a', fontWeight: '600', textDecoration: 'none' }}>
                    +971 2 409 3159
                  </a>
                </div>
              </div>
            </div>

            {/* Actions Button */}
            <button 
              type="button"
              onClick={() => setIsUserDeleted(false)}
              style={{
                width: '100%',
                padding: '14px',
                backgroundColor: '#0f172a',
                color: '#ffffff',
                border: 'none',
                borderRadius: '10px',
                fontWeight: '700',
                fontSize: '0.95rem',
                cursor: 'pointer',
                transition: 'background-color 0.2s',
                boxShadow: '0 4px 12px rgba(15, 23, 42, 0.15)'
              }}
            >
              {t('close', 'Close')}
            </button>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes shake { 0%,100%{transform:translateX(0)} 25%{transform:translateX(-10px)} 75%{transform:translateX(10px)} }
        .input-error { border-color:#dc3545!important; background-color:#fff8f8!important; }
        .spinner { width:16px;height:16px;border:2px solid #fff;border-top:2px solid transparent;border-radius:50%;animation:spin .8s linear infinite;display:inline-block;margin-right:8px; }
        @keyframes spin { 0%{transform:rotate(0deg)} 100%{transform:rotate(360deg)} }
        .login-btn-submit.loading { opacity:.7;cursor:not-allowed; }
      `}</style>
    </div>
  );
};

export default LoginForm;